import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { ServiceSettings } from '@models/settings/service-settings';
import { ServiceSettingsQueryService } from '@query/serviceSettingsQueryService';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private readonly _serviceSettingsQuery = inject(ServiceSettingsQueryService);
  private readonly _createMutation = this._serviceSettingsQuery.createServiceSettings();

  private readonly _query = this._serviceSettingsQuery.getServiceSettings({
    params: { expand: 'ui_settings' },
  });

  private readonly _settingsSignal = signal<ServiceSettings | undefined>(undefined);

  private readonly _currentSettings = computed(() => {
    const data = this._query.data();
    return data?.results?.[0];
  });

  public readonly settings = computed(() => {
    return this._settingsSignal() || this._currentSettings();
  });

  public readonly isLoaded = computed(() => {
    return !this._query.isPending() && !this._query.isFetching();
  });

  constructor() {
    effect(() => {
      const settings = this._currentSettings();
      const isLoaded = this.isLoaded();

      if (isLoaded) {
        if (settings) {
          this._settingsSignal.set(settings);
        } else {
          this._createDefaultSettings();
        }
      }
    });
  }

  /**
   * Get default settings object
   */
  private _getDefaultSettings(): ServiceSettings {
    return {
      uuid: '',
      name: '',
      service_url: '',
      email_settings: '',
      ui_settings: {
        enableLibraryUIV2: false,
        uuid: '',
      },
      use_team_validation: false,
    } as ServiceSettings;
  }

  /**
   * Create default settings if none exist
   * First tries to create via API, falls back to local defaults on error
   */
  private _createDefaultSettings(): void {
    this._createMutation.mutate(
      {
        name: 'Базовая конфигурация',
        use_team_validation: false,
      },
      {
        onSuccess: response => {
          this._settingsSignal.set(response);
        },
        onError: () => {
          this._settingsSignal.set(this._getDefaultSettings());
        },
      }
    );
  }

  /**
   * Load settings (for backward compatibility)
   * Now it's just a no-op since query loads automatically
   */
  public async loadSettings(): Promise<void> {
    return Promise.resolve();
  }

  /**
   * Get current settings (synchronous)
   * @returns Current settings or default settings if not loaded
   */
  public getSettings(): ServiceSettings {
    return this.settings() || this._getDefaultSettings();
  }

  /**
   * Manually update settings in local state
   * Used after mutations to immediately reflect changes
   */
  public setSettings(settings: ServiceSettings): void {
    this._settingsSignal.set(settings);
  }
}
